"use client";
import { useEffect, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import clsx from "clsx";
import { OTPInput, REGEXP_ONLY_DIGITS, SlotProps } from "input-otp";
import { PhoneIcon } from "@phosphor-icons/react";
import { cn, fetchOTP, verifyOTP } from "@/lib/utils";
import { useFormDataStore, useFormStateStore } from "@/components/context/useFormContext";
import { secureInput } from "@/lib/security";
import ErrorAlert from "@/components/ui/errorAlert";

export const PhoneValidation = () => {
	const { formValues, setFormValue, setFormError, formErrors } = useFormDataStore();
	const { setFormErrors, setPhoneVerified, setValidateFormDetails, hasAttemptedSubmit } =
		useFormStateStore();
	const { phone } = formValues;
	const [errorMsg, setErrMsg] = useState("");

	const [code, setCode] = useState("");
	const [txId, setTxId] = useState("");
	const [errors, setErrors] = useState({
		phone: false,
	});

	function handlePhoneChange(e: React.ChangeEvent<HTMLInputElement>) {
		const rawValue = e.target.value.replace(/[^0-9]/g, "");

		const { sanitized, isValid } = secureInput.phone(rawValue);

		setFormValue("phone", sanitized);

		setErrors((prev) => ({ ...prev, phone: !isValid }));

		if (rawValue.trim() === "") {
			setErrMsg("Phone number is required");

			return;
		}

		if (!isValid) {
			setFormError("phone", "Incorrect format");

			return;
		}

		if (isValid) {
			setFormError("phone", "");
		}
	}

	// Checks whether any of the fields have errors. If it does, zustand's formError is set to true
	useEffect(() => {
		const hasError = Object.values(errors).some((error) => error);
		setFormErrors(hasError);
	}, [errors, setFormErrors]);

	// Function to validate all fields
	const validateForm = () => {
		const newErrors = {
			phone: !secureInput.phone(phone).isValid,
		};

		if (phone.trim() === "") {
			setFormError("phone", "Phone number is required");
		}

		setErrors(newErrors);

		// If any error exists, set formError to true
		const hasError = Object.values(newErrors).some((error) => error);
		setFormErrors(hasError);

		return !hasError; // Returns true if validation passes
	};

	// Store the validation function in Zustand so QuickForm can call it
	useEffect(() => {
		setValidateFormDetails(validateForm);
	}, [phone]);

	const sendOTP = useMutation({
		mutationKey: [formValues.phone],
		mutationFn: async () => {
			const res = await fetchOTP(formValues.phone);
			return res;
		},
		onSuccess: async (data) => {
			setTxId(data.id);
		},
		onError: async (err: any) => {
			setErrMsg(err.message);
		},
	});

	const confirmOTP = useMutation({
		mutationKey: [txId, code],
		mutationFn: async () => {
			if (!txId || !code) {
				throw new Error("OTP code is required for verification.");
			}
			const res = await verifyOTP(txId, code);
			return res;
		},
		onSuccess(data) {
			if (data.status == 200) {
				setPhoneVerified(true);
			}
		},
		onError: (err: any) => {
			setErrMsg(err.message);
		},
	});

	const handleOTPChange = (code: string) => {
		setCode(code);
	};

	// Reset form errors on focus
	const handleFocus = (field: keyof typeof errors) => {
		setErrors((prev) => ({ ...prev, [field]: false }));
		setFormError("phone", "");
		setErrMsg("");
	};

	/*
	Changed component to always display the phone input field, users can now edit their number
	and resend the verification code if needed. Added a resend code button and changed the layout
*/

	return (
		<>
			<div className="cC-py-0 cC-space-y-3">
				<div className="cC-relative cC-w-full">
					<div className="cC-relative cC-flex sm:cC-flex-1 cC-rounded-md cC-shadow-sm">
						<div className="cC-pointer-events-none cC-absolute cC-inset-y-0 cC-left-0 cC-z-10 cC-flex cC-items-center cC-pl-3">
							<PhoneIcon size={25} weight="regular" className="cC-text-gray-400"/>
						</div>
						<input
							name="phone-number"
							id="phone-number"
							placeholder="04XX-XXX-XXX"
							aria-describedby="phone-number"
							aria-label="Phone Number"
							inputMode="numeric"
							autoComplete="tel"
							maxLength={10}
							onFocus={() => handleFocus("phone")}
							value={phone}
							onChange={(e) => {
								handlePhoneChange(e);
								// Reset OTP sent status if user modifies phone number after sending code
								if (sendOTP.isSuccess) {
									sendOTP.reset?.();
								}
							}}
							onBlur={(e) => {
								let value = e.target.value.replace(/[^0-9]/g, "");
								setFormValue("phone", value);

								setErrors((prev) => ({
									...prev,
									phone: value.trim() !== "" && !secureInput.phone(value).isValid,
								}));
							}}
							className={clsx(
								errors.phone ||
									sendOTP.isError ||
									(hasAttemptedSubmit && formErrors.phone)
									? "cC-ring-red-500 cC-ring-1 focus:cC-ring-red-500"
									: "focus:cC-ring-[--cC-primary]",
								"cC-block cC-w-full cC-rounded-md cC-border-0 cC-py-2.5 cC-pl-12 cC-pr-12 cC-text-gray-900 cC-ring-1 cC-ring-inset cC-ring-gray-300 placeholder:cC-text-gray-400 focus:cC-ring-1 focus:cC-ring-inset focus-visible:cC-outline-[--cC-primary] sm:cC-text-md sm:cC-leading-6",
							)}
						/>
					</div>
					{formErrors?.phone && (
						<span className="cC-text-xs cC-text-red-500">{formErrors.phone}</span>
					)}
					{errorMsg && sendOTP.error && (
						<span className="cC-text-xs cC-text-red-500">{errorMsg}</span>
					)}
				</div>

				{/* Send Code Button / Resend Option */}
				<div className="cC-relative cC-flex cC-rounded-md cC-shadow-sm">
					<button
						className={clsx(
							sendOTP.isPending
								? "cC-bg-gray-200 hover:cC-cursor-not-allowed cC-text-gray-500 cC-ring-gray-100 hover:cC-bg-gray-200"
								: "cC-bg-[--cC-primary] hover:cC-bg-[--cC-primary-darker]",
							"cC-inline-flex cC-w-full cC-justify-center cC-rounded-md cC-mt-1 cC-px-3 cC-py-2.5 cC-text-md cC-font-semibold cC-text-white cC-shadow-sm cC-duration-200 focus-visible:cC-outline focus-visible:cC-outline-1 focus-visible:cC-outline-offset-1 focus-visible:cC-outline-gray-900",
						)}
						disabled={sendOTP.isPending}
						onClick={(e) => {
							e.preventDefault();
							const isValid = validateForm();
							if (!isValid) {
								if (hasAttemptedSubmit) {
									setErrMsg("");
								} else {
									setErrMsg("Phone number is required");
									return;
								}
							}

							if (formValues.phone && !errors.phone) {
								sendOTP.mutate();
							}
						}}
					>
						{sendOTP.isPending ? (
							<div
								role="status"
								className="cC-flex cC-flex-col cC-w-full cC-h-full cC-justify-center cC-items-center"
							>
								<svg
									aria-hidden="true"
									className="cC-w-8 cC-h-8 cC-text-[--cC-primary] cC-animate-spin cC-fill-white"
									viewBox="0 0 100 101"
									fill="none"
									xmlns="http://www.w3.org/2000/svg"
								>
									<path
										d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
										fill="currentColor"
									/>
									<path
										d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
										fill="currentFill"
									/>
								</svg>
								<span className="cC-sr-only">Loading...</span>
							</div>
						) : sendOTP.isSuccess ? (
							"Resend Code"
						) : (
							"Send Verification Code"
						)}
					</button>
				</div>

				{/* Conditionally display OTP input and verify button once code is sent */}
				{sendOTP.isSuccess && ( // Render the OTP input and verify button only if the OTP has been sent successfully
					<div className="cC-grid cC-w-full cC-gap-2 cC-items-start cC-pt-2">
						<div className="cC-group cC-flex cC-max-w-full cC-flex-1 cC-items-center cC-justify-center">
							<OTPInput // Render the OTPInput component with a custom render function to display the slots in two rows with a dash in between
								autoFocus
								onChange={handleOTPChange}
								value={confirmOTP.isError ? "" : code}
								maxLength={4}
								aria-label="Verification Code"
								containerClassName="cC-max-w-full cC-text-[--cC-secondary] cC-group cC-text-gray-900 cC-flex cC-items-center cC-justify-center has-[:disabled]:cC-opacity-30"
								render={(
									{ slots }, // Render the slots in two rows with a dash in between
								) => (
									<>
										<div className="cC-flex">
											{slots.slice(0, 2).map((slot, idx) => (
												<Slot key={idx} {...slot} />
											))}
										</div>

										<FakeDash />

										<div className="cC-flex">
											{slots.slice(2).map((slot, idx) => (
												<Slot key={idx} {...slot} />
											))}
										</div>
									</>
								)}
								inputMode="numeric"
								pattern={REGEXP_ONLY_DIGITS}
							/>
						</div>

						<button
							className={clsx(
								confirmOTP.isPending
									? "cC-bg-gray-200 hover:cC-cursor-not-allowed cC-text-gray-500 cC-ring-gray-100 hover:cC-bg-gray-200"
									: "cC-bg-[--cC-primary] hover:cC-bg-[--cC-primary-darker]",
								"cC-inline-flex cC-w-full cC-justify-center cC-rounded-md cC-px-3 cC-py-2.5 cC-mt-3 cC-text-md cC-font-semibold cC-text-white cC-shadow-sm cC-duration-200 focus-visible:cC-outline focus-visible:cC-outline-1 focus-visible:cC-outline-offset-1 focus-visible:cC-outline-gray-900",
							)}
							disabled={confirmOTP.isPending}
							onClick={async (e) => {
								e.preventDefault();
								confirmOTP.error ? setCode("") : confirmOTP.mutate();
							}}
						>
							{confirmOTP.isPending ? (
								<div
									role="status"
									className="cC-flex cC-flex-col cC-w-full cC-h-full cC-justify-center cC-items-center"
								>
									<svg
										aria-hidden="true"
										className="cC-w-8 cC-h-8 cC-text-[--cC-primary] cC-animate-spin cC-fill-white"
										viewBox="0 0 100 101"
										fill="none"
										xmlns="http://www.w3.org/2000/svg"
									>
										<path
											d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
											fill="currentColor"
										/>
										<path
											d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
											fill="currentFill"
										/>
									</svg>
									<span className="cC-sr-only">Loading...</span>
								</div>
							) : (
								"Verify Code"
							)}
						</button>
					</div>
				)}
			</div>
			{confirmOTP.isError && <ErrorAlert text={errorMsg} />}
		</>
	);
};

function Slot(props: SlotProps) {
	//
	return (
		<div
			className={cn(
				"cC-relative cC-w-10 cC-h-14 max-md:cC-w-[3rem] max-md:cC-h-14 cC-text-[2rem]",
				"cC-flex cC-items-center cC-justify-center",
				"cC-transition-all cC-duration-300",
				"!cC-border !cC-border-solid !cC-border-gray-300",
				"!cC-border-y !cC-border-r !first:cC-border-l first:cC-rounded-l-md last:cC-rounded-r-md",
				"!group-hover:cC-border-[--cC-primary] !group-focus-within:cC-border-[--cC-primary] !group-focus-within:cC-border-1",
				"!cC-outline !cC-outline-0 !cC-outline-[--cC-primary]",
				{ "!cC-outline-2 !cC-outline-[--cC-primary]": props.isActive },
			)}
		>
			{props.char !== null && <div>{props.char}</div>}
			{props.hasFakeCaret && <FakeCaret />}
		</div>
	);
}

function FakeCaret() {
	return (
		<div className="cC-absolute cC-pointer-events-none cC-inset-0 cC-flex cC-items-center cC-justify-center cC-animate-caret-blink">
			<div className="cC-w-px cC-h-8 cC-bg-white" />
		</div>
	);
}

function FakeDash() {
	return (
		<div className="cC-flex cC-w-10 cC-justify-center cC-items-center">
			<div className="cC-w-3 cC-h-1 cC-rounded-full cC-bg-border cC-bg-[--cC-primary]" />
		</div>
	);
}
